import React, { useState } from 'react'
import { Box, Divider } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { useReactiveVar } from '@apollo/client'
import { ProviderDialog } from '../../system-container/provider-dialog/ProviderDialog'
import {
  BuildingFragment,
  LocalGridType,
  ProviderFragment,
} from '../../../generated/graphql'
import { useProviders } from '../../../provider/use-providers'
import {
  convertFormParametersToInputParameters,
  convertProviderParametersToFormParameters,
  FormValues,
} from '../../system-container/provider-dialog/form-values'
import { ProviderList } from '../../system-container/energy-system/ProviderList'
import {
  EnergyType,
  mapEnergyTypeToDemandType,
} from '../../../calculators/EnergyFlow'
import { BuildingUsageMatrix } from './BuildingUsageMatrix'
import {
  activeVersionVar,
  buildingEnergyTypeVar,
  projectVar,
} from '../../../apollo/reactive-vars'
import {
  electricityProviderTypes,
  getProviderTypesByEnergyType,
  localEnergyProviderTypes,
  providerProperties,
  staticProviderTypes,
} from '../../../provider/provider-properties'
import { useAlert } from '../../alert/AlertProvider'
import { VisibleEnergyTypeSelect } from '../../system-container/VisibleEnergyTypeSelect'
import { sortProvidersByPriority } from '../../../calculators/providers/util/provider-utility'
import { SingleSettingSection } from '../../common/SingleSettingSection'
import { SpecialDemand } from '../../../calculators/special-demand-ids'
import { BuildingEnergySystemCharts } from './BuildingEnergySystemCharts'
import { useAuthorization } from '../../../authorization/use-authorization'

type Props = {
  building: BuildingFragment
}

export function BuildingEnergySystemTab({
  building,
}: Props): React.ReactElement {
  const { createProvider, updateProvider, deleteProvider } = useProviders()
  const [isCreatingProvider, setIsCreatingProvider] = useState(false)
  const [editingProvider, setEditingProvider] = useState<
    ProviderFragment | undefined
  >()
  const project = useReactiveVar(projectVar)
  const activeVersion = useReactiveVar(activeVersionVar)
  const buildingEnergyType = useReactiveVar(buildingEnergyTypeVar)
  const { t } = useTranslation()
  const { alert } = useAlert()
  const canEditProject = useAuthorization().canEditProject(project)

  const onCreateSubmit = async (values: FormValues) => {
    if (!values.type) {
      return
    }

    setIsCreatingProvider(false)

    const demandTypesForProvider = providerProperties[
      values.type
    ].providesEnergyTypes.map(mapEnergyTypeToDemandType)

    await createProvider(building, undefined, {
      name: values.name ?? '',
      type: values.type,
      parameters: convertFormParametersToInputParameters(values.parameters),
      hourSeriesIds: values.hourSeriesIds ?? [],
      performanceTableIds: values.performanceTableIds ?? [],
      temperatureCurveIds: values.temperatureCurveIds ?? [],
      demands: building.demands
        .filter((demand) => demandTypesForProvider.includes(demand.type))
        .map((demand) => ({ demandId: demand.id, maxPortion: 1 })),
      providesElectricityForProviders: electricityProviderTypes.includes(
        values.type,
      ),
    })
  }

  const onEditSubmit = async (values: FormValues) => {
    if (!editingProvider) {
      return
    }

    setEditingProvider(undefined)

    await updateProvider(
      editingProvider.id,
      {
        name: values.name,
        type: values.type,
        parameters: convertFormParametersToInputParameters(values.parameters),
        hourSeriesIds: values.hourSeriesIds,
        performanceTableIds: values.performanceTableIds,
        temperatureCurveIds: values.temperatureCurveIds,
      },
      building,
    )
  }

  const onDelete = () => {
    if (!editingProvider) {
      return
    }

    alert(
      t('building.tabs.energySystem.deleteDialog.title'),
      t('building.tabs.energySystem.deleteDialog.message'),
      [
        { title: t('common.cancel') },
        {
          title: t('building.tabs.energySystem.deleteDialog.confirm'),
          color: 'secondary',
          onPress: async () => {
            setEditingProvider(undefined)
            await deleteProvider(building, undefined, editingProvider.id)
          },
        },
      ],
    )
  }

  const onEditAllocation = async (
    provider: ProviderFragment,
    demandId: string,
    maxPortion: number,
  ) => {
    if (staticProviderTypes.includes(provider.type)) {
      return
    }

    if (demandId === SpecialDemand.OtherProvidersElectricity) {
      await updateProvider(
        provider.id,
        {
          providesElectricityForProviders: maxPortion > 0,
        },
        building,
      )
      return
    }

    const connections = provider.demands
      .map((providerDemand) => ({
        demandId: providerDemand.demand.id,
        maxPortion: providerDemand.maxPortion,
      }))
      .filter((connection) => connection.demandId !== demandId)

    if (maxPortion !== 0) {
      connections.push({
        demandId,
        maxPortion,
      })
    }

    await updateProvider(provider.id, { demands: connections }, building)
  }

  if (!project || !activeVersion) {
    return <div />
  }

  const localElectricalGridExists = activeVersion.localGrids.some(
    (grid) => grid.type === LocalGridType.Electricity,
  )
  const localWaterGridExists = activeVersion.localGrids.some(
    (grid) => grid.type === LocalGridType.Water,
  )

  const visibleDemandType = mapEnergyTypeToDemandType(buildingEnergyType)
  const visibleProviderTypes = getProviderTypesByEnergyType(
    buildingEnergyType,
    localElectricalGridExists,
    localWaterGridExists,
  )
  const addableProviderTypes = visibleProviderTypes.filter(
    (type) =>
      !staticProviderTypes.includes(type) &&
      !localEnergyProviderTypes.includes(type),
  )

  const visibleProviders = building.providers
    .filter((provider) => visibleProviderTypes.includes(provider.type))
    .sort(sortProvidersByPriority)

  const currentProviderNames = building.providers.map(
    (provider) => provider.name,
  )

  return (
    <Box>
      <ProviderDialog
        isOpen={isCreatingProvider}
        defaultValues={{ parameters: {} }}
        projectId={project.id}
        providerTypes={addableProviderTypes}
        hourSeriesCollections={project.hourSeriesCollections}
        demands={building.demands}
        buildingParts={building.parts}
        currentProviderNames={currentProviderNames}
        onClose={() => setIsCreatingProvider(false)}
        onSubmit={onCreateSubmit}
      />

      <ProviderDialog
        isOpen={editingProvider !== undefined}
        defaultValues={{
          name: editingProvider?.name,
          type: editingProvider?.type,
          parameters: convertProviderParametersToFormParameters(
            editingProvider?.parameters,
            building.demands,
          ),
          hourSeriesIds:
            editingProvider?.hourSeries.map((hourSeries) => hourSeries.id) ??
            [],
        }}
        projectId={project.id}
        providerTypes={visibleProviderTypes}
        hourSeriesCollections={project.hourSeriesCollections}
        demands={building.demands}
        buildingParts={building.parts}
        isFallbackProvider={editingProvider?.isFallback}
        currentProviderNames={currentProviderNames}
        onClose={() => setEditingProvider(undefined)}
        onSubmit={onEditSubmit}
        onDelete={onDelete}
      />

      <SingleSettingSection title={t('building.tabs.energySystem.type')}>
        <VisibleEnergyTypeSelect
          options={[
            EnergyType.Heating,
            EnergyType.Cooling,
            EnergyType.Electricity,
          ]}
          value={buildingEnergyType}
          onChange={(value) => buildingEnergyTypeVar(value)}
        />
      </SingleSettingSection>

      <Divider />

      <ProviderList
        providers={visibleProviders}
        onAdd={() => setIsCreatingProvider(true)}
        onEdit={(provider) => setEditingProvider(provider)}
        editDisabled={!canEditProject}
        energyType={buildingEnergyType}
      />

      <BuildingUsageMatrix
        building={building}
        providers={visibleProviders}
        energyType={buildingEnergyType}
        visibleDemandType={visibleDemandType}
        onEditAllocation={onEditAllocation}
        editDisabled={!canEditProject}
      />

      <Divider />

      <BuildingEnergySystemCharts
        building={building}
        energyType={buildingEnergyType}
      />
    </Box>
  )
}
